import { t, Trans } from '@lingui/macro'
import {
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  Grid,
  MenuItem,
  Typography
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { ReduxOrganization, ReduxState, ReduxUser } from 'app/redux/ReduxInterfaces'
import { DialogTitleWithIconClose } from 'app/views/common-components/DialogTitleWithIconClose'
import { TextFieldWithFormik } from 'app/views/common-components/with-formik/TextFieldWithFormik'
import { getTranslationIdFromSalesforceApiValue } from 'app/views/common/translationsCommon'
import { FormElementTitle } from 'app/views/forms/common/FormElementTitle'
import { requiredTrans } from 'app/views/forms/formTranslations'
import { Formik } from 'formik'
import { useSnackbar } from 'notistack'
import { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import { useLocation } from 'react-router-dom'
import { myI18n } from 'translation/I18nConnectedProvider'
import * as yup from 'yup'
import {
  CaseData,
  CaseType,
  createCase,
  createCaseByFlow,
  getCaseTypes
} from '../../services/sfAuth/sfData/sfCase'
import { getCaseTypesForFundingStreams } from '../../services/sfAuth/sfData/sfFundingStream'

import { Case } from 'app/backend/salesforce-maps/Case'
import { HELP_CASE_TYPES } from '../../services/sfAuth/sfData/sfCaseTypes'

const labels = HELP_CASE_TYPES
  ? {
      [HELP_CASE_TYPES.HELP]: {
        titleTranslation: <Trans>HELP_REQUEST_DIALOG_TITLE</Trans>,
        topTranslations: '',
        describeTranslation: <Trans>HELP_REQUEST_DETAILS</Trans>,
        subjectFieldLabel: <Trans>HELP_REQUEST_SUBJECT_FIELD</Trans>,
        descriptionFieldLabel: <Trans>HELP_REQUEST_DETAILS_FIELD</Trans>
      },
      [HELP_CASE_TYPES.FUNDING_UNSURE]: {
        thankYouTranslation: <Trans>CONTACT_PM_THANK_YOU</Trans>,
        titleTranslation: <Trans>CONTACT_PM_TITLE</Trans>,
        topTranslations: '',
        subjectFieldLabel: <Trans>CONTACT_PM_SUBJECT</Trans>,
        describeTranslation: <Trans>CONTACT_PM_DESCRIPTION</Trans>
      }
    }
  : {}

interface InquiryTypes {
  disabled?: boolean
  open: boolean
  handleClose: () => void
  caseType?: string | null
  selectCaseType?: boolean
  selectCaseLocation?: boolean
  translations?: {
    titleTranslation?: JSX.Element
    topTranslations?: JSX.Element
    describeTranslation?: JSX.Element
    subjectFieldLabel?: JSX.Element
  }
  surveyId?: string
  isESAT?: boolean
  additionalValues?: Partial<Case>
}

export default function Inquiry({
  open,
  handleClose,
  selectCaseType = false,
  selectCaseLocation = false,
  caseType,
  surveyId,
  isESAT = false,
  translations = {},
  disabled = false,
  additionalValues = {}
}: InquiryTypes): JSX.Element {
  const [caseSubmitted, setCaseSubmitted] = useState<boolean>(false)
  const location = useLocation()
  const user = useSelector<ReduxState, ReduxUser>((state) => state.user)
  const organization = useSelector<ReduxState, ReduxOrganization>((state) => state.organization)

  const { data: caseData, isLoading: isCaseTypesLoading } = useQuery<CaseData>(
    ['caseTypes'],
    getCaseTypes,
    {
      enabled: selectCaseType
    }
  )
  const { data: caseTypesFromFundingStreams = [], isLoading: isFundingStreamCaseTypesLoading } =
    useQuery<CaseType[]>(['fundingStreamCaseTypes'], getCaseTypesForFundingStreams, {
      enabled: selectCaseType
    })
  const { caseTypes = [], caseLocations = [] } = caseData ?? {}
  const { enqueueSnackbar } = useSnackbar()
  const typeTranslations = caseType != null && caseType in labels ? labels[caseType] : {}
  const {
    titleTranslation,
    topTranslations,
    describeTranslation,
    subjectFieldLabel,
    descriptionFieldLabel,
    locationTranslation,
    thankYouTranslation
  } = {
    thankYouTranslation: <Trans>CASE_SUBMITTED_THANK_YOU_TEXT</Trans>,
    locationTranslation: <Trans>CASE_LOCATION</Trans>,
    titleTranslation: <Trans>How can we help you?</Trans>,
    topTranslations: myI18n._(t`Subject`),
    subjectFieldLabel: <Trans>Describe your problem here</Trans>,
    describeTranslation: (
      <Trans>Please, fill additional info to help us address your problem</Trans>
    ),
    descriptionFieldLabel: myI18n._(t`Description`),
    ...translations,
    ...typeTranslations
  }

  const validCaseTypes = [
    'Portal - help',
    'External reviewers - info about the process',
    'Self-assessment tool',
    'Resource Inventory'
  ]

  const caseTypesFiltered = caseTypes
    .filter((caseType) => validCaseTypes.includes(caseType.value))
    .map((caseType) => {
      const parsed = getTranslationIdFromSalesforceApiValue(caseType.value)
      return {
        ...caseType,
        label: parsed
      }
    })

  const mergedCaseTypes = [...caseTypesFiltered, ...caseTypesFromFundingStreams]

  useEffect(() => {
    setCaseSubmitted(false)
  }, [open])

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitleWithIconClose
        label={caseSubmitted ? <Trans>THANK_YOU</Trans> : titleTranslation}
        handleClose={handleClose}
        disabled={false}
      />
      <Formik
        enableReinitialize
        validationSchema={yup.object().shape({
          title: yup.string().required(requiredTrans).max(250),
          description: yup.string().required(requiredTrans).max(3000),
          inquiryType: yup.string().nullable().required(requiredTrans),
          region: selectCaseLocation
            ? yup.string().nullable().required(requiredTrans)
            : yup.string().nullable()
        })}
        initialValues={{
          title: '',
          description: '',
          inquiryType: caseType,
          region: ''
        }}
        onSubmit={async (values, { setSubmitting, resetForm }) => {
          enqueueSnackbar(<Trans>Submitting Case</Trans>, {
            variant: 'info'
          })
          const data = {
            ...values,
            origin: 'Portal',
            comments:
              `url: ${location.pathname} \n env: v ${process.env.REACT_APP_VERSION}-` +
              `${process.env.REACT_APP_ENVIROMENT}`,
            type: values.inquiryType || '',
            language: user.language,
            organization: organization.id,
            contact: user.userObject.contactId,
            surveyId,
            location: location.pathname,
            isESAT,
            ...additionalValues
          }
          const createPromise = isESAT ? createCaseByFlow : createCase
          return await createPromise(data)
            .then((r) => {
              enqueueSnackbar(<Trans>CASE_SUBMITTED</Trans>, {
                variant: 'success'
              })
              setSubmitting(false)
              resetForm({})
              setCaseSubmitted(true)
            })
            .catch((error) => {
              console.error('Error submitting case', error)
              enqueueSnackbar(<Trans>CASE_ERROR_SUBMISSION</Trans>, {
                variant: 'error'
              })
              setSubmitting(false)
            })
        }}
      >
        {({ isValid, dirty, handleSubmit, isSubmitting, values, resetForm }) => {
          return (
            <DialogContent sx={{ paddingLeft: '48px', paddingRight: '48px' }}>
              {caseSubmitted ? (
                <>
                  <DialogContentText>{thankYouTranslation}</DialogContentText>
                </>
              ) : (
                <>
                  <Typography className="required-fields-label" sx={{ mb: '8px' }}>
                    <Trans>REQUIRED_FIELDS_PROMPRT_EDITABLE_FORM</Trans>
                  </Typography>
                  {selectCaseType && (
                    <>
                      <FormElementTitle
                        id="inquiryType"
                        title={topTranslations}
                        disabled={isSubmitting}
                        required
                      />
                      <TextFieldWithFormik
                        select
                        disabled={isSubmitting}
                        name="inquiryType"
                        fullWidth
                        variant="outlined"
                        required
                        sx={{ mb: '32px' }}
                      >
                        {mergedCaseTypes.map((obj) => {
                          return (
                            <MenuItem value={obj.value} key={obj.value}>
                              <Trans id={obj.label} />
                            </MenuItem>
                          )
                        })}
                      </TextFieldWithFormik>
                    </>
                  )}

                  {selectCaseLocation && (
                    <>
                      <FormElementTitle
                        id="region"
                        title={locationTranslation}
                        disabled={isSubmitting}
                        required
                      />
                      <TextFieldWithFormik
                        select
                        disabled={isSubmitting}
                        name="region"
                        fullWidth
                        variant="outlined"
                        required
                        sx={{ mb: '32px' }}
                      >
                        {caseLocations.map((obj) => (
                          <MenuItem value={obj.value} key={obj.value}>
                            {myI18n._(obj.label)}
                          </MenuItem>
                        ))}
                      </TextFieldWithFormik>
                    </>
                  )}

                  <FormElementTitle
                    id="title"
                    title={subjectFieldLabel}
                    disabled={isSubmitting}
                    required
                  />
                  <TextFieldWithFormik
                    id="title"
                    variant="outlined"
                    fullWidth
                    name="title"
                    required
                    limit={250}
                    disabled={isSubmitting}
                    sx={{ mb: '32px' }}
                  />

                  <FormElementTitle
                    id="description"
                    title={describeTranslation}
                    disabled={isSubmitting}
                    required
                  />
                  <TextFieldWithFormik
                    id="description"
                    variant="outlined"
                    fullWidth
                    multiline
                    rows={6}
                    required
                    name="description"
                    disabled={isSubmitting}
                    limit={3000}
                  />
                </>
              )}

              <Grid sx={{ mt: '32px' }} container direction="column" gap="16px">
                {!caseSubmitted && (
                  <Button
                    variant="contained"
                    disabled={
                      !isValid ||
                      isSubmitting ||
                      !dirty ||
                      isCaseTypesLoading ||
                      isFundingStreamCaseTypesLoading
                    }
                    color="primary"
                    onClick={() => {
                      handleSubmit()
                    }}
                  >
                    <Trans>Submit</Trans>
                  </Button>
                )}
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    handleClose()
                    resetForm({})
                  }}
                >
                  <Trans>Cancel</Trans>
                </Button>
              </Grid>
            </DialogContent>
          )
        }}
      </Formik>
    </Dialog>
  )
}
